import type { ReactNode, Ref } from "react";

import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useProfileLayoutMobileSidebar } from "../layout";
import { ProfileMobileSidebar } from "./profile-mobile-sidebar";

export interface ProfileScrollAreaProps {
  /**
   * When supplied, the underlying ScrollArea forwards the ref to its viewport
   * (the actual scrollable element). Used by callers that need programmatic
   * scroll (e.g. arbitrator review and tasks task-profile use it to drive
   * IntersectionObserver-based active-section pinning).
   */
  viewportRef?: Ref<HTMLDivElement>;
  /**
   * Override the viewport className applied to the ScrollArea viewport. When
   * `viewportRef` is supplied (the arbitrator/task variant) the default is
   * `"overscroll-contain scroll-smooth"`. When `viewportRef` is absent (the
   * search/pipeline shape) the viewport receives no additional classes; the
   * outer container takes the `overflow-y-auto overscroll-contain` styling
   * directly.
   */
  viewportClassName?: string;
  /**
   * Forwarded to ScrollArea. Independent of `viewportRef`; both shapes opt
   * into it. Defaults to `false`. The underlying ScrollArea applies
   * `{ minWidth: 0, width: "100%" }` to its content when this is `true` -
   * useful for vertical-scroll-only surfaces.
   */
  constrainContentWidth?: boolean;
  /**
   * Override the outer container className. Merged via `cn()` over the base
   * `"min-h-0 min-w-0 flex-1 bg-surface-elevated"` (plus the
   * `"overflow-y-auto overscroll-contain"` variant when `viewportRef` is
   * absent).
   */
  className?: string;
  children: ReactNode;
}

/**
 * Shared profile scroll-area primitive used by the five profile entry points
 * (arbitrator/review, jobs/pipeline/profile, search/profile,
 * candidate/components/profile/skeleton, tasks/task-profile).
 *
 * Two shapes:
 * - viewportRef ABSENT - the search/pipeline shape. The outer container takes
 *   `"min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain bg-surface-elevated"`.
 * - viewportRef PRESENT - the arbitrator/task variant. The outer container
 *   takes `"min-h-0 min-w-0 flex-1 bg-surface-elevated"` and the viewport gets
 *   `"overscroll-contain scroll-smooth"` (override via `viewportClassName`).
 */
export function ProfileScrollArea({
  viewportRef,
  viewportClassName,
  constrainContentWidth = false,
  className,
  children,
}: ProfileScrollAreaProps) {
  const hasViewportRef = viewportRef !== undefined;
  const base = hasViewportRef
    ? "min-h-0 min-w-0 flex-1 bg-surface-elevated"
    : "min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain bg-surface-elevated";
  const resolvedViewportClassName = hasViewportRef
    ? (viewportClassName ?? "overscroll-contain scroll-smooth")
    : viewportClassName;

  const mobileSidebar = useProfileLayoutMobileSidebar();

  return (
    <ScrollArea
      className={cn(base, className)}
      viewportRef={viewportRef}
      viewportClassName={resolvedViewportClassName}
      constrainContentWidth={constrainContentWidth}
    >
      {children}
      {mobileSidebar ? (
        <ProfileMobileSidebar>{mobileSidebar}</ProfileMobileSidebar>
      ) : null}
    </ScrollArea>
  );
}
